import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useSession } from "../lib/auth";
import { api } from "../lib/api";
import { useInviteValidation } from "../hooks/useInviteValidation";
import {
  Button,
  Input,
  Label,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@wondermaps/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus } from "@fortawesome/pro-solid-svg-icons/faUserPlus";
import { faSpinner } from "@fortawesome/pro-solid-svg-icons/faSpinner";
import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck";

export const Route = createFileRoute("/join")({
  component: JoinOrgPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      code: (search.code as string) || undefined,
    };
  },
});

function JoinOrgPage() {
  const navigate = useNavigate();
  const { code: codeFromUrl } = useSearch({ from: "/join" });
  const { data: session, isPending: isSessionLoading } = useSession();

  const [inviteCode, setInviteCode] = useState(codeFromUrl ?? "");
  const [error, setError] = useState<string | null>(null);

  const {
    isValid,
    orgName,
    role,
    isLoading: isValidating,
  } = useInviteValidation(inviteCode);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isSessionLoading && !session?.user) {
      navigate({
        to: "/login",
        search: { redirect: codeFromUrl ? `/join?code=${codeFromUrl}` : "/join" },
      });
    }
  }, [isSessionLoading, session, navigate, codeFromUrl]);

  const redeemMutation = useMutation({
    mutationFn: async () => {
      const res = await api.invites[":code"].redeem.$post({
        param: { code: inviteCode },
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(
          (data as { error?: string }).error || "Failed to join organization"
        );
      }

      return res.json();
    },
    onSuccess: (data) => {
      // Redirect to org admin dashboard
      if (data.success && data.organizationSlug) {
        navigate({ to: `/${data.organizationSlug}/admin` });
      }
    },
    onError: (err) => {
      setError(
        err instanceof Error ? err.message : "Failed to join organization"
      );
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!inviteCode.trim()) {
      setError("Please enter an invite code");
      return;
    }

    if (!isValid) {
      setError("Please enter a valid invite code");
      return;
    }

    redeemMutation.mutate();
  };

  // Loading state
  if (isSessionLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="mb-2 h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!session?.user) {
    return null;
  }

  const isLoading = redeemMutation.isPending;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <FontAwesomeIcon icon={faUserPlus} className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">Join Organization</CardTitle>
          <CardDescription>
            Enter your invite code to join an organization
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invite-code">Invite Code</Label>
              <Input
                id="invite-code"
                type="text"
                value={inviteCode}
                onChange={(e) => {
                  setInviteCode(e.target.value);
                  setError(null);
                }}
                placeholder="e.g., ZOO-ABC123"
                disabled={isLoading}
              />
              {inviteCode && (
                <div className="text-sm">
                  {isValidating ? (
                    <span className="text-muted-foreground flex items-center gap-1">
                      <FontAwesomeIcon icon={faSpinner} spin className="h-3 w-3 " />
                      Validating...
                    </span>
                  ) : isValid && orgName ? (
                    <span className="text-green-600 flex items-center gap-1">
                      <FontAwesomeIcon icon={faCheck} className="h-3 w-3" />
                      {orgName} ({role})
                    </span>
                  ) : inviteCode.length > 0 ? (
                    <span className="text-destructive">Invalid invite code</span>
                  ) : null}
                </div>
              )}
            </div>

            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || !isValid}
            >
              {isLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="h-4 w-4 mr-2 " />
                  Joining...
                </>
              ) : (
                "Join Organization"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
